import type { ClientReviewStatusV1 } from "@enrein/shared-contracts/approval-change";
import type { StudioBusinessGraphStatusV1 } from "@enrein/shared-contracts/studio-project-workflow";
import type { StudioMissionControlSummaryV1, StudioRunRowV1 } from "@enrein/shared-contracts/studio-dashboard";
import { isQaFailureRunStatus } from "./features/enrein-guided-ui/adapters/guided-project-steps.js";
import { canSharePreviewFromClientLinkStatus } from "./resolve-share-preview-url.js";

export type StudioActionTarget = "new" | "scan" | "project" | "proof";

export type StudioNextAction = {
  readonly id: string;
  readonly title: string;
  readonly detail: string;
  readonly target: StudioActionTarget;
  readonly priority: number;
};

/**
 * Single-project mission control queue (legacy cmd-room detail).
 * Portfolio-level Command Center cards: deriveGuidedNextActions in guided-project-steps.
 */
export function deriveStudioNextActions(input: {
  readonly hasDraft: boolean;
  readonly hasActiveRevision: boolean;
  readonly intakeSubmitted: boolean;
  readonly selectedProjectId: string | null;
  readonly runs: readonly StudioRunRowV1[];
  readonly clientReview: ClientReviewStatusV1 | null;
  readonly bgStatus: StudioBusinessGraphStatusV1 | null;
  readonly missionSummary: StudioMissionControlSummaryV1 | null;
}): readonly StudioNextAction[] {
  const actions: StudioNextAction[] = [];

  if (!input.selectedProjectId) {
    actions.push({
      id: "intake",
      title: "Create project",
      detail: "Start a new client project and Business Profile draft.",
      target: "new",
      priority: 1,
    });
    return actions.slice(0, 4);
  }

  if (input.intakeSubmitted && !input.hasDraft && !input.hasActiveRevision) {
    actions.push({
      id: "intake-draft",
      title: "Create draft from client intake",
      detail: "Client submitted intake — promote their facts into a Business Profile draft.",
      target: "project",
      priority: 1,
    });
  }

  if (input.hasDraft && !input.hasActiveRevision) {
    actions.push({
      id: "activate",
      title: "Validate and activate draft",
      detail: "Complete Business Profile validation before generation.",
      target: "project",
      priority: 2,
    });
  }

  if (input.hasActiveRevision && input.runs.length === 0) {
    actions.push({
      id: "generate",
      title: "Generate site",
      detail: "Run the first generation for the active Business Profile revision.",
      target: "project",
      priority: 3,
    });
  }

  const latestRun = input.runs[0];
  if (latestRun && (latestRun.qaRollup === "FAIL" || isQaFailureRunStatus(latestRun.status))) {
    actions.push({
      id: "qa-review",
      title: "Review quality gate failure",
      detail: "Inspect the latest run and resolve blocking issues.",
      target: "project",
      priority: 4,
    });
  }

  if (input.clientReview?.clientLink && canSharePreviewFromClientLinkStatus(input.clientReview.clientLink.status)) {
    actions.push({
      id: "client-link",
      title: "Share client review link",
      detail: "Copy the client review room link for approval.",
      target: "project",
      priority: 5,
    });
  }

  if ((input.missionSummary?.handoffReadyCount ?? 0) > 0) {
    actions.push({
      id: "handoff",
      title: "Prepare handoff",
      detail: "Projects are ready for Departure Kit packaging.",
      target: "proof",
      priority: 6,
    });
  }

  if (actions.length === 0) {
    actions.push({
      id: "workbench",
      title: "Open project workbench",
      detail: "Review proof chain and next packaging step.",
      target: "project",
      priority: 7,
    });
  }

  return actions.sort((a, b) => a.priority - b.priority).slice(0, 4);
}

export type ProofChainStepState = "complete" | "active" | "pending" | "blocked";

export type ProofChainStep = {
  readonly id: string;
  readonly label: string;
  readonly status: string;
  readonly detail: string;
  readonly state: ProofChainStepState;
};

export function deriveProofChainSteps(input: {
  readonly hasReport: boolean;
  readonly clientReview: ClientReviewStatusV1 | null;
  readonly hasLaunchProof: boolean;
  readonly handoffReady: boolean;
}): readonly ProofChainStep[] {
  const previewReady = Boolean(input.clientReview?.latestPreview);
  const approved = Boolean(input.clientReview?.latestApproval);

  return [
    {
      id: "scan",
      label: "Scan",
      status: input.hasReport ? "Readiness attached" : "Not available",
      detail: input.hasReport ? "Search readiness report is available." : "Generate a report when checks complete.",
      state: input.hasReport ? "complete" : "pending",
    },
    {
      id: "preview",
      label: "Preview",
      status: previewReady ? "Preview shared" : "Not shared",
      detail: previewReady ? "Client preview has been loaded." : "Create a client link to share preview.",
      state: previewReady ? "complete" : input.hasReport ? "active" : "pending",
    },
    {
      id: "approval",
      label: "Approval",
      status: approved ? "Approved" : previewReady ? "Waiting" : "Not started",
      detail: approved
        ? "Client approval is on record."
        : previewReady
          ? "Waiting for client approval via portal."
          : "Share preview before approval.",
      state: approved ? "complete" : previewReady ? "active" : "pending",
    },
    {
      id: "proof",
      label: "Proof",
      status: input.hasLaunchProof ? "Launch proof ready" : "In progress",
      detail: input.hasLaunchProof ? "Internal launch proof checklist available." : "Complete proof requirements.",
      state: input.hasLaunchProof ? "complete" : approved ? "active" : "pending",
    },
    {
      id: "dk",
      label: "Departure Kit",
      status: input.handoffReady ? "Ready to package" : "Not ready",
      detail: input.handoffReady ? "Handoff export can proceed when proof passes." : "Proof and approval required first.",
      state: input.handoffReady ? "complete" : input.hasLaunchProof ? "active" : "pending",
    },
  ];
}
